import { $handleRichTextDrop, $insertDataTransferForRichText } from '@lexical/clipboard';
import { eventFiles, mergeRegister, objectKlassEquals } from '@lexical/utils';
import { sanitizePastedHTML } from '@pbk-web/utils';
import type { LexicalEditor } from 'lexical';
import { $getSelection, COMMAND_PRIORITY_HIGH, CONTROLLED_TEXT_INSERTION_COMMAND, DROP_COMMAND, PASTE_COMMAND } from 'lexical';

import { tableWidthsFromPercent } from '../../serialization/tableWidths';

/** Lexical's own node JSON: any page can put one on the clipboard or a drag, and it would create nodes unchecked. */
const LEXICAL_NODES_MIME = 'application/x-lexical-editor';
/** Only names the editor a drag started in, so a drop can remove the dragged range (a move); it carries no content. */
const LEXICAL_DRAG_MIME = 'application/x-lexical-drag';

const parser = new DOMParser();

/** Imported HTML as it may enter the editor: allow-listed, with Froala's percentage table widths converted. */
export const cleanImportedHtml = (html: string): string => {
  const dom = parser.parseFromString(sanitizePastedHTML(html), 'text/html');
  tableWidthsFromPercent(dom);
  return dom.body.innerHTML;
};

/**
 * A stand-in for a paste's or drop's `DataTransfer` with only what may be imported:
 * sanitised HTML, plain text, URIs, the drag marker and files — never Lexical's node JSON.
 */
const sanitizedTransfer = (source: DataTransfer): DataTransfer => {
  const html = source.getData('text/html');
  const allowed: Record<string, string> = {
    'text/html': html ? cleanImportedHtml(html) : '',
    'text/plain': source.getData('text/plain'),
    'text/uri-list': source.getData('text/uri-list'),
    [LEXICAL_DRAG_MIME]: source.getData(LEXICAL_DRAG_MIME),
  };

  return {
    getData: (type: string) => allowed[type] ?? '',
    types: Array.from(source.types).filter((type) => type !== LEXICAL_NODES_MIME),
    files: source.files,
  } as unknown as DataTransfer;
};

/** The drop event with its `dataTransfer` swapped, for Lexical's drop handling (which also reads the drop point). */
const withTransfer = (event: DragEvent, dataTransfer: DataTransfer): DragEvent =>
  new Proxy(event, {
    get: (target, key) => {
      if (key === 'dataTransfer') return dataTransfer;
      const value: unknown = Reflect.get(target, key, target);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });

/**
 * Everything imported into rich text — pasted or dropped — goes through `sanitizePastedHTML`, then Lexical's usual import.
 * - Lexical's node JSON is never read (see `LEXICAL_NODES_MIME`). Copying or dragging within the editor
 *   round-trips through its HTML instead, which the allow-list keeps intact.
 * - Covers the three ways content arrives: a paste, a drop of a drag that began in a Lexical editor
 *   (`DROP_COMMAND`, a move), and the browser's own drop (`CONTROLLED_TEXT_INSERTION_COMMAND`).
 * - Files (images) are left to `RichTextPlugin`, which hands them to `ImageDropPlugin`.
 * - Plain mode needs none of this: `PlainTextPlugin` only ever reads `text/plain`.
 */
export const registerImportSanitizer = (editor: LexicalEditor): (() => void) =>
  mergeRegister(
    editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!objectKlassEquals(event, ClipboardEvent)) return false;
        const clipboard = (event as ClipboardEvent).clipboardData;
        if (!clipboard) return false;

        const [, files, hasText] = eventFiles(event);
        if (files.length > 0 && !hasText) return false;
        // Pasting into an input inside the content (e.g. a form in a decorator) is the browser's job
        if (event.target instanceof HTMLElement && event.target.closest('input, textarea')) return false;

        event.preventDefault();
        const selection = $getSelection();
        if (selection) $insertDataTransferForRichText(sanitizedTransfer(clipboard), selection);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    ),
    editor.registerCommand(
      DROP_COMMAND,
      (event) => {
        const [, files] = eventFiles(event);
        if (files.length > 0 || !event.dataTransfer) return false;
        // Without a drag marker this declines, and the drop arrives as the browser's insertion below
        return $handleRichTextDrop(withTransfer(event, sanitizedTransfer(event.dataTransfer)), editor);
      },
      COMMAND_PRIORITY_HIGH,
    ),
    editor.registerCommand(
      CONTROLLED_TEXT_INSERTION_COMMAND,
      (eventOrText) => {
        // Typed text is a string; only a drop (or yank / replacement) brings a `DataTransfer`
        if (typeof eventOrText === 'string' || !eventOrText.dataTransfer) return false;
        const selection = $getSelection();
        if (!selection) return false;

        $insertDataTransferForRichText(sanitizedTransfer(eventOrText.dataTransfer), selection);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    ),
  );
